import React, { useEffect } from 'react';
import { Router } from 'react-router-dom';
import { connect } from 'react-redux';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { GetLoggedInUser, InitApp } from 'actions';
import { IAppState } from 'types';
import history from './helpers/history';
import { useAuth0 } from './react-auth0-spa';
import usePolicyNotification from 'hooks/usePolicyNotification';
import LayoutContainer from 'containers/layout';
import useUserLoaded from 'hooks/useUserLoaded';
import useBundleNotification from 'hooks/useBundleNotification';
import useRenewalNotification from 'hooks/useRenewalNotification';
import AppBanner from 'components/app-banner';
import Loading from 'components/loading';
import { growthBook, growthBookInit } from './services/growthbook-service';
import { DefaultRoutes, OnboardingRoutes } from 'routes';
import { isOnboardingUser } from './helpers/user';
import { windowScrollTop } from './helpers/window';
import { PROFILE_ROUTE } from 'constants/routes';
import RootModalContainer from 'containers/root-modals';
import SessionExpiredContainer from 'containers/auth/SessionExpiredContainer';
import { analytics } from './analytics';

interface IDispatchProps {
  initApp(): void;
  getLoggedInUser(): void;
}

const App = ({
  user,
  getLoggedInUser,
  initApp,
}: IAppState & IDispatchProps) => {
  const { authError, isAuthenticated, loginWithRedirect } = useAuth0();
  const isOnboarding = isOnboardingUser(user);

  useUserLoaded();
  useBundleNotification();
  usePolicyNotification();
  useRenewalNotification();

  useEffect(() => {
    if (isAuthenticated) {
      getLoggedInUser();
      initApp();
    } else {
      loginWithRedirect({ appState: { targetUrl: window.location.pathname } });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user.loaded) {
      growthBookInit(user);
    }
  }, [user.loaded]);

  useEffect(() => {
    // Track initial page view
    analytics.page();

    // Scroll to top and track page view on every route change except back navigation
    const unlisten = history.listen((locationState, action) => {
      if (action !== 'POP' && !locationState.pathname.includes(PROFILE_ROUTE)) {
        windowScrollTop();
      }
      analytics.page();
    });

    return () => {
      unlisten();
    };
  }, []);

  if (!isAuthenticated) {
    // Auth still resolving (silent auth takes ~1-2s on a full page load) or
    // redirecting to the login page - show the same loading screen as the
    // user-fetch phase below instead of a blank page.
    return authError ? <SessionExpiredContainer /> : <Loading full={true} />;
  }

  if (!user.loaded) {
    return <Loading full={true} />;
  }

  return (
    <Router history={history}>
      <GrowthBookProvider growthbook={growthBook}>
        {isOnboarding ? (
          <OnboardingRoutes />
        ) : (
          <>
            <RootModalContainer />
            <AppBanner />
            {isAuthenticated && (
              <LayoutContainer>
                <DefaultRoutes />
              </LayoutContainer>
            )}
          </>
        )}
      </GrowthBookProvider>
    </Router>
  );
};

const mapStateToProps = (state: IAppState) => ({
  ...state,
});

const mapDispatchToProps = {
  getLoggedInUser: GetLoggedInUser,
  initApp: InitApp,
};

export default connect<IAppState, IDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(App);
